import { useCallback, useState } from 'react';

/** `set` minus `time`, handing `set` back untouched when it isn't there so the model memo holds. */
function withoutTime(set: ReadonlySet<string>, time: string): ReadonlySet<string> {
  if (!set.has(time)) return set;
  const next = new Set(set);
  next.delete(time);
  return next;
}

/** Hands back the same empty set, so a reset of an untouched grid doesn't rebuild its model. */
const cleared = (set: ReadonlySet<string>): ReadonlySet<string> =>
  set.size === 0 ? set : new Set();

/**
 * Rows ("HH:mm") added or taken out of a grid by hand, shared by every grid with an
 * "Agregar horario" row.
 *
 * View state only: it never reaches the backend, so it needs no saving, discarding or
 * leave-without-saving guard. A reload brings back what the schedule declares, and a row that
 * holds a match stays on the axis without help, because the match itself puts it there.
 */
export function useGridTimeRows() {
  const [extraTimes, setExtraTimes] = useState<ReadonlySet<string>>(new Set());
  const [hiddenTimes, setHiddenTimes] = useState<ReadonlySet<string>>(new Set());

  // Adding a time that was hidden brings it back, same as "Mostrar".
  const addTime = useCallback((time: string) => {
    setExtraTimes((prev) => new Set(prev).add(time));
    setHiddenTimes((prev) => withoutTime(prev, time));
  }, []);

  // Both sets change: dropping it from `extraTimes` alone isn't enough when the schedule also
  // declares that time, since the declaration keeps it on the axis.
  const removeTime = useCallback((time: string) => {
    setExtraTimes((prev) => withoutTime(prev, time));
    setHiddenTimes((prev) => new Set(prev).add(time));
  }, []);

  const showHiddenTimes = useCallback(() => setHiddenTimes(cleared), []);

  /** Forgets the hide of `times`, e.g. the rows a match brought back on screen. */
  const unhideTimes = useCallback(
    (times: readonly string[]) => setHiddenTimes((prev) => times.reduce(withoutTime, prev)),
    [],
  );

  const resetTimes = useCallback(() => {
    setExtraTimes(cleared);
    setHiddenTimes(cleared);
  }, []);

  return { extraTimes, hiddenTimes, addTime, removeTime, showHiddenTimes, unhideTimes, resetTimes };
}
